export type ValueWithUnit = { value: number; unit: string }

const isVwu = (x: unknown): x is ValueWithUnit =>
  typeof x === 'object' && x !== null &&
  typeof (x as ValueWithUnit).value === 'number' &&
  typeof (x as ValueWithUnit).unit === 'string'

/**
 * The elevation of the END of `vertex` that a connection touches.
 *
 * ⚠️ Elevation is declared per vertex schema, NOT on a shared base, and it comes in
 * three shapes:
 *   1. a single required `elevation` — `junction`, `tee`, `bend`, `tank`, most others;
 *   2. two required ends with NO single value — `centrifugal_pump` has
 *      `suction_elevation` + `discharge_elevation`;
 *   3. two `.optional()` ends that are frequently absent — `check_valve`, `strainer`,
 *      `fixed_loss` (`inlet_elevation`/`outlet_elevation`; optional because the C++
 *      side never emits these Connect-only devices).
 *
 * Reading a bare `vertex.elevation` yields `undefined` on shapes 2 and 3, which a
 * caller then coerces to 0 — silently injecting a static-head error that is invisible
 * on the canvas, because canvas Y carries no elevation information.
 *
 * `side` is the role the EDGE gives this vertex: an edge whose `source_id` is this
 * vertex LEAVES it (discharge/outlet); one whose `target_id` is this vertex ARRIVES
 * (suction/inlet).
 *
 * Returns `undefined` when nothing is declared. Callers must walk further along the run
 * or surface a default — never coerce to 0.
 */
export function elevationAt(vertex: unknown, side: 'source' | 'target'): ValueWithUnit | undefined {
  if (typeof vertex !== 'object' || vertex === null) return undefined
  const v = vertex as Record<string, unknown>

  if (isVwu(v['elevation'])) return v['elevation']

  const leaving = side === 'source'
  const twoEnded = leaving
    ? [v['discharge_elevation'], v['outlet_elevation']]
    : [v['suction_elevation'], v['inlet_elevation']]
  for (const candidate of twoEnded) if (isVwu(candidate)) return candidate

  return undefined
}

/**
 * The elevation `t` of the way along a run between two known ends.
 *
 * Returns `undefined` when either end is unknown, or when the units differ — adding a
 * metre to a foot numerically is worse than admitting we do not know.
 */
export function interpolateElevation(
  a: ValueWithUnit | undefined,
  b: ValueWithUnit | undefined,
  t: number,
): ValueWithUnit | undefined {
  if (!a || !b) return undefined
  if (a.unit !== b.unit) return undefined
  return { value: a.value + (b.value - a.value) * t, unit: a.unit }
}
